import classNames from 'classnames';
import sumBy from 'lodash/sumBy';
import * as React from 'react';
import { getPointsLabel } from '../../Utils/studyModelGridUtils';
import type {
    StudyModelGridSemestersProps,
    StudyModelGridSemestersTranslations,
} from '../StudyModelGridSemesters/StudyModelGridSemesters';
import { StudyModelGridSemesters } from '../StudyModelGridSemesters/StudyModelGridSemesters';
import { Collapse } from '~/Partials/Collapse/Collapse';
import { ComboSelector } from '~/Partials/ComboSelector/ComboSelector';
import { hasAny } from '~/Partials/Utils/utils';
import styles from './StudyModelGridContent.module.css';

export interface StudyModelGridContentTranslations extends StudyModelGridSemestersTranslations {
    specialization: string;
    studyOptions: string;
    studyPointsFormat: string;
}

export interface StudyModelGridContentProps
    extends Pick<
        StudyModelGridSemestersProps,
        'selectedYearProps' | 'currentYearSubjects' | 'getSubjectInfoUrl' | 'overlayContentMessages'
    > {
    specializationOptions: Map<string, string>;
    studyOptions: Map<string, string>;
    onSpecializationSelected: (newValue: string) => void;
    onStudyOptionSelected: (newValue: string) => void;
    translations: StudyModelGridContentTranslations;
    selectedSpecialization?: string;
    selectedStudyOption?: string;
}

export const StudyModelGridContent: React.FC<StudyModelGridContentProps> = props => {
    const {
        specializationOptions,
        selectedSpecialization,
        onSpecializationSelected,
        selectedStudyOption,
        onStudyOptionSelected,
        studyOptions,
        currentYearSubjects,
        translations,
        ...semesterViewProps
    } = props;
    const { specialization, studyOptions: studyOptionsLabel, studyPointsFormat, ...restTranslations } = translations;

    const currentYearPoints = sumBy(currentYearSubjects, subject => subject.points);

    const hasAnySpecializations = hasAny(currentYearSubjects, subject => !!subject.specialization);
    const hasAnyStudyOptions = hasAny(currentYearSubjects, subject => !!subject.studyOption);

    const pointsLabel = getPointsLabel(studyPointsFormat, currentYearPoints);

    return (
        <div className={styles.StudyModelGridContent}>
            {specializationOptions.size > 0 && (
                <Collapse
                    isOpen={hasAnySpecializations}
                    className={classNames(styles.SelectorContainer, styles.SpecializationSelectorContainer)}
                >
                    <ComboSelector
                        name="specialization-selector"
                        options={specializationOptions}
                        currentValue={selectedSpecialization}
                        onChange={onSpecializationSelected}
                        label={`${specialization}:`}
                    />
                </Collapse>
            )}
            {studyOptions.size > 0 && (
                <Collapse
                    isOpen={hasAnyStudyOptions}
                    className={classNames(styles.SelectorContainer, styles.OptionSelectorContainer)}
                >
                    <ComboSelector
                        name="study-option-selector"
                        options={studyOptions}
                        currentValue={selectedStudyOption}
                        onChange={onStudyOptionSelected}
                        label={`${studyOptionsLabel}:`}
                        className={styles.OptionSelector}
                    />
                </Collapse>
            )}
            {pointsLabel && <span className={styles.StudyPoints}>{pointsLabel}</span>}
            <StudyModelGridSemesters
                {...semesterViewProps}
                currentYearSubjects={currentYearSubjects}
                translations={{
                    ...restTranslations,
                    specialization,
                }}
                className={styles.Semesters}
            />
        </div>
    );
};
